import { Coins, DollarSign, Gift, TrendingUp, Star, Zap, ArrowRight, Box, Trophy, Ticket, Percent, CloudRain, Calendar } from 'lucide-react';
import Footer from '../components/Footer';

const Rewards = () => {
  const conversionTable = [
    { deposit: 30, coins: 42.86, bonus: 42.86, total: 85.72, usdValue: 60 },
    { deposit: 40, coins: 57.14, bonus: 57.14, total: 114.28, usdValue: 80 },
    { deposit: 50, coins: 71.43, bonus: 71.43, total: 142.86, usdValue: 100 },
    { deposit: 60, coins: 85.71, bonus: 85.71, total: 171.42, usdValue: 120 },
    { deposit: 70, coins: 100, bonus: 100, total: 200, usdValue: 140 },
  ];

  const otherBonuses = [
    {
      icon: <Gift className="w-5 h-5 sm:w-6 sm:h-6" />,
      title: 'First Deposit Bonus',
      description: '100% match on your first deposit up to 100 coins ($70)',
      extra: 'Use code CS2026',
      highlight: true,
    },
    {
      icon: <Box className="w-5 h-5 sm:w-6 sm:h-6" />,
      title: 'Daily Case',
      description: 'Reach account milestones and unlock daily free cases.',
    },
    {
      icon: <Zap className="w-5 h-5 sm:w-6 sm:h-6" />,
      title: 'Supercharged Cases',
      description: 'Deposit 10 coins and get supercharged cases for higher winning chances.',
    },
    {
      icon: <Trophy className="w-5 h-5 sm:w-6 sm:h-6" />,
      title: 'Rankings / Leaderboards',
      description: 'Compete with other players. Win a share of 2,500 coins every 24 hours.',
    },
    {
      icon: <Ticket className="w-5 h-5 sm:w-6 sm:h-6" />,
      title: 'Giveaways',
      description: 'Every 100 coins wagered earns 1 raffle ticket for exclusive giveaways.',
    },
    {
      icon: <Percent className="w-5 h-5 sm:w-6 sm:h-6" />,
      title: 'Rakeback',
      description: 'Daily, weekly, and monthly rakeback. Wager and get a % of house edge back.',
    },
    {
      icon: <CloudRain className="w-5 h-5 sm:w-6 sm:h-6" />,
      title: 'The Rain',
      description: 'Every 60 minutes, a percentage of all wagers is distributed to active players.',
    },
    {
      icon: <Calendar className="w-5 h-5 sm:w-6 sm:h-6" />,
      title: 'Seasonal Events',
      description: 'During festivities, CSGOLuck hosts events with exclusive rewards.',
    },
  ];

  return (
    <div className="min-h-screen bg-csgo-bg pt-20 sm:pt-24 md:pt-32 pb-16">
      <div className="fixed inset-0 bg-grid opacity-20 pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12" data-testid="rewards-header">
          <h1 className="font-orbitron font-black text-2xl sm:text-4xl md:text-5xl text-white mb-3 sm:mb-4">
            CSGOLuck <span className="text-csgo-primary">Rewards</span>, Bonuses &amp; <span className="text-csgo-secondary">Promotions</span>
          </h1>
          <p className="font-rajdhani text-sm sm:text-lg text-csgo-textMuted max-w-xl mx-auto">
            Understand all benefits CSGOLuck bonuses and promotions offer.
          </p>
        </div>

        {/* Coin Conversion Explanation */}
        <div className="bg-csgo-surface/80 backdrop-blur-md border border-csgo-border rounded-2xl p-5 sm:p-6 md:p-8 mb-6 sm:mb-8" data-testid="conversion-info">
          <div className="flex items-start gap-3 sm:gap-4 mb-5 sm:mb-6">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-csgo-secondary/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <Coins className="w-5 h-5 sm:w-6 sm:h-6 text-csgo-secondary" />
            </div>
            <div>
              <h2 className="font-orbitron font-bold text-base sm:text-xl text-white mb-1 sm:mb-2">
                How the CSGOLuck Coin System Works
              </h2>
              <p className="font-rajdhani text-xs sm:text-base text-csgo-textMuted">
                Instead of playing with real money, CSGOLuck uses a coin system. Here is how it works:
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            {[
              { label: 'Exchange Rate', value: '5 Coins', sub: '= $3.50' },
              { label: 'Per Coin', value: '1 Coin', sub: '= $0.70' },
              { label: 'Min Deposit', value: '$30', sub: 'for bonus', highlight: 'secondary' },
              { label: 'Max Bonus', value: '100 Coins', sub: '= $70 Value', highlight: 'primary' },
            ].map((item, i) => (
              <div key={i} className={`p-3 sm:p-4 rounded-xl border text-center ${
                item.highlight === 'primary' 
                  ? 'bg-csgo-primary/10 border-csgo-primary/30' 
                  : item.highlight === 'secondary'
                  ? 'bg-csgo-secondary/10 border-csgo-secondary/30'
                  : 'bg-csgo-bg border-csgo-border'
              }`}>
                <p className="font-rajdhani text-csgo-textMuted text-xs mb-1">{item.label}</p>
                <p className={`font-orbitron font-bold text-base sm:text-2xl ${
                  item.highlight === 'primary' ? 'text-csgo-primary' 
                  : item.highlight === 'secondary' ? 'text-csgo-secondary'
                  : 'text-white'
                }`}>{item.value}</p>
                <p className={`font-rajdhani text-xs sm:text-sm ${
                  item.highlight ? `text-csgo-${item.highlight}` : 'text-csgo-primary'
                }`}>{item.sub}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Conversion Table */}
        <div className="bg-csgo-surface/60 backdrop-blur-sm border border-csgo-border rounded-xl overflow-hidden mb-6 sm:mb-8" data-testid="conversion-table">
          <div className="p-4 sm:p-6 border-b border-csgo-border">
            <h3 className="font-orbitron font-bold text-base sm:text-xl text-white flex items-center gap-2 sm:gap-3">
              <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-csgo-primary" />
              Deposit &amp; Bonus Calculator
            </h3>
            <p className="font-rajdhani text-xs sm:text-base text-csgo-textMuted mt-1">
              See exactly what you get with our 100% bonus (up to 100 coins)
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[400px]">
              <thead>
                <tr className="bg-csgo-bg/50">
                  {['Deposit', 'Coins', 'Bonus', 'Total Coins', 'Total Value'].map((h, i) => (
                    <th key={i} className={`px-3 sm:px-6 py-3 sm:py-4 text-left font-rajdhani font-semibold uppercase text-xs ${
                      i === 2 ? 'text-csgo-primary' : i === 3 ? 'text-csgo-secondary' : i === 4 ? 'text-white' : 'text-csgo-textMuted'
                    }`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {conversionTable.map((row, index) => (
                  <tr 
                    key={index} 
                    className={`border-t border-csgo-border hover:bg-csgo-surface/50 transition-colors ${row.deposit === 70 ? 'bg-csgo-primary/5' : ''}`}
                  >
                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                      <span className="font-orbitron font-bold text-sm sm:text-base text-white">${row.deposit}</span>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                      <span className="font-rajdhani text-xs sm:text-sm text-csgo-textMuted">{row.coins.toFixed(2)}</span>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                      <span className="font-orbitron font-bold text-xs sm:text-sm text-csgo-primary">+{Math.min(row.bonus, 100).toFixed(2)}</span>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                      <span className="font-orbitron font-bold text-xs sm:text-sm text-csgo-secondary">{Math.min(row.coins + 100, row.total).toFixed(2)}</span>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                      <span className="font-orbitron font-bold text-xs sm:text-sm text-white">${Math.min(row.usdValue, row.deposit + 70)}</span>
                      {row.deposit === 70 && (
                        <span className="ml-1 sm:ml-2 text-xs bg-csgo-primary text-black px-1.5 sm:px-2 py-0.5 rounded-full font-rajdhani font-semibold">
                          MAX
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Other CSGOLuck Bonuses Section */}
        <div className="mb-8 sm:mb-12" data-testid="other-bonuses">
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="font-orbitron font-bold text-xl sm:text-2xl md:text-3xl text-white mb-2">
              Other CSGOLuck <span className="text-csgo-primary">Bonuses</span> &amp; Promotions
            </h2>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {otherBonuses.map((bonus, index) => (
              <div
                key={index}
                className={`p-4 sm:p-5 rounded-xl border transition-all duration-300 card-hover ${
                  bonus.highlight 
                    ? 'bg-csgo-primary/10 border-csgo-primary/30' 
                    : 'bg-csgo-surface/60 border-csgo-border'
                }`}
              >
                <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center mb-2 sm:mb-3 ${
                  bonus.highlight ? 'bg-csgo-primary/20 text-csgo-primary' : 'bg-csgo-bg text-csgo-textMuted'
                }`}>
                  {bonus.icon}
                </div>
                <h3 className="font-orbitron font-bold text-sm sm:text-base text-white mb-1 sm:mb-2">{bonus.title}</h3>
                <p className="font-rajdhani text-xs sm:text-sm text-csgo-textMuted">{bonus.description}</p>
                {bonus.extra && (
                  <div className="mt-2 sm:mt-3 inline-flex items-center gap-1 sm:gap-1.5 text-csgo-primary font-rajdhani font-semibold text-xs">
                    <Zap className="w-3 h-3" />
                    {bonus.extra}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center bg-csgo-surface/80 backdrop-blur-md border border-csgo-border rounded-2xl p-6 sm:p-8" data-testid="rewards-cta">
          <h2 className="font-orbitron font-bold text-xl sm:text-2xl text-white mb-3 sm:mb-4">
            Ready to Claim Your <span className="text-csgo-primary">$70 Welcome Bonus</span>?
          </h2>
          <p className="font-rajdhani text-sm sm:text-base text-csgo-textMuted mb-5 sm:mb-6">
            Redeem the code CS2026 and deposit $70 to get the maximum bonus.
          </p>
          <a
            href="https://csgoluck.com/r/CS2026"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="rewards-claim-btn"
            className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-csgo-primary text-black font-orbitron font-bold text-sm sm:text-base uppercase tracking-wider rounded-xl hover:bg-csgo-primary/90 transition-all duration-200 neon-glow hover:scale-105 w-full sm:w-auto"
          >
            Claim Bonus Now
            <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
          </a>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Rewards;
